import { __awaiter } from 'tslib';
import { im_proto } from '../../proto/index.js';
import { WebSocketLevel, ErrorType } from '../consts.js';
import { BaseError } from '../errors/BaseError.js';
import { Logger } from '../metrics/logger.js';
import { Monitor, MonitorItem } from '../metrics/monitor.js';
import { NetworkItem } from '../network/NetworkItem.js';
import { ContextClassBase, ServiceIdentity } from '../service/RuntimeContext.js';

class BaseApiManager extends ContextClassBase {
    constructor(ctx) {
        super(ctx);
    }
    send(body, cmd, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.sendWithRawBody(body, cmd, options)).body;
        });
    }
    // 发送请求, 提供超时 + 自动重试 + 公共异常处理能力, 抽象给其他插件用
    sendWithRawBody(body, cmd, options = {}) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = Monitor.performanceNow();
            const reqTrackerContext = this.getReqTrackerContext(body);
            let item;
            try {
                if (options.inboxType === undefined) {
                    options.inboxType = this.resolve(ServiceIdentity.InboxType).getDefaultInboxWithoutCheck();
                }
                if (this.ctx.option.webSocketLevel === WebSocketLevel.PushOnly) {
                    options.forceHttp = true;
                }
                if (this.ctx.option.webSocketLevel === WebSocketLevel.All) {
                    options.forceHttp = false;
                }
                item = new NetworkItem(this.ctx, cmd, body);
                yield item.prepareRequest(options.inboxType);
                // 如果识别出来是 forceHttp, 那么给 maxHttpRetry 设置成 max, maxWsRetry 设置成 0
                // 否则给 maxWsRetry 设置成 max, maxHttp 设置成 undefined
                yield this.resolve(ServiceIdentity.NetworkManager).send(item, {
                    maxHttpRetryTimes: options.forceHttp === true ? options.maxRetryTimes : undefined,
                    maxWsRetryTimes: options.forceHttp === true ? 0 : options.maxRetryTimes,
                    useBeacon: options.useBeacon
                });
                // 使用beacon时不上报打点, 避免无限循环
                if (options.useBeacon || item.response === undefined) {
                    return im_proto.Response.create({});
                }
                const resp = item.response;
                if (resp.status_code !== 0) {
                    const errType = resp.status_code;
                    switch (errType) {
                        case ErrorType.Degradation:
                            throw new BaseError({
                                ctx: this.ctx,
                                msg: 'server degradation',
                                type: ErrorType.Degradation,
                                sender: this,
                                logid: resp === null || resp === void 0 ? void 0 : resp.log_id,
                                args: {
                                    req: body,
                                    resp
                                }
                            });
                        case ErrorType.InvalidTicket:
                            if (cmd === im_proto.IMCMD.SEND_MESSAGE) {
                                // FIXME: 不止 SEND_MESSAGE 会带ticket
                                const convId = body.send_message_body.conversation_id;
                                const conv = this.resolve(ServiceIdentity.ConversationManager).get(convId);
                                const oldTicket = conv.ticket;
                                yield this.resolve(ServiceIdentity.ConversationManager).refreshTicket(conv.id);
                                const newTicket = conv.ticket;
                                if (oldTicket === newTicket || options.isRetryReq) {
                                    // ticket刷新了还是发不出去, 抛出错误
                                    throw new BaseError({
                                        ctx: this.ctx,
                                        msg: `invalid ticket for conv: ${convId}`,
                                        type: ErrorType.InvalidTicket,
                                        sender: this,
                                        logid: resp === null || resp === void 0 ? void 0 : resp.log_id,
                                        args: {
                                            req: body,
                                            resp
                                        }
                                    });
                                }
                                else {
                                    Logger.ctxDebug(this.ctx, 'refresh ticket due to invalid');
                                    // await 之后会有新的ticket, 重新发一遍
                                    return this.sendWithRawBody(body, cmd, Object.assign(Object.assign({}, options), { isRetryReq: true }));
                                }
                            }
                            break;
                        case ErrorType.InvalidToken:
                        case ErrorType.ExpiredToken:
                            const oldToken = this.ctx.cachedToken;
                            try {
                                this.resolve(ServiceIdentity.NetworkManager).closeWs(true); // close outdated ws
                                // maybe session failed, try to use http and close ws
                                if (oldToken === '' || this.ctx.option.authType === im_proto.AuthType.SESSION_AUTH) {
                                    if (options.isRetryReq) {
                                        // if second try failed, return error
                                        throw new BaseError({
                                            ctx: this.ctx,
                                            msg: 'invalid session',
                                            type: ErrorType.TokenFuncError,
                                            sender: this,
                                            logid: resp === null || resp === void 0 ? void 0 : resp.log_id,
                                            args: {
                                                req: body,
                                                resp
                                            }
                                        });
                                    }
                                    else {
                                        Logger.ctxDebug(this.ctx, 'http retry due to invalid session');
                                        return this.sendWithRawBody(body, cmd, Object.assign(Object.assign({}, options), { isRetryReq: true, forceHttp: true }));
                                    }
                                }
                                else {
                                    // for token auth, refresh token and retry
                                    yield this.resolve(ServiceIdentity.AuthManager).refreshToken();
                                    const newToken = this.ctx.cachedToken;
                                    if (oldToken === newToken || options.isRetryReq) {
                                        throw new BaseError({
                                            ctx: this.ctx,
                                            msg: `token refresh fail: ${newToken}`,
                                            type: ErrorType.TokenFuncError,
                                            sender: this,
                                            logid: resp === null || resp === void 0 ? void 0 : resp.log_id,
                                            args: {
                                                req: body,
                                                resp
                                            }
                                        });
                                    }
                                    else {
                                        Logger.ctxDebug(this.ctx, 'refresh token due to invalid');
                                        return this.sendWithRawBody(body, cmd, Object.assign(Object.assign({}, options), { isRetryReq: true }));
                                    }
                                }
                            }
                            catch (e) {
                                if (e.type !== undefined) {
                                    e.args = Object.assign(Object.assign({}, e.args), { req: body, resp });
                                    throw e;
                                }
                                else {
                                    throw new BaseError({
                                        ctx: this.ctx,
                                        msg: 'token refresh func error',
                                        type: ErrorType.TokenFuncError,
                                        innerError: e,
                                        sender: this,
                                        logid: resp === null || resp === void 0 ? void 0 : resp.log_id,
                                        args: {
                                            req: body,
                                            resp
                                        }
                                    });
                                }
                            }
                        default:
                            if (options.ignoreBizError) {
                                this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.NetworkRequest, {
                                    duration: Monitor.performanceNow() - startTime,
                                    retry_times: (_a = item === null || item === void 0 ? void 0 : item.retryTimes) !== null && _a !== void 0 ? _a : 0
                                }, {
                                    imsdk_cmd: cmd.toString(),
                                    imsdk_result: '0',
                                    seq_id: (_b = item === null || item === void 0 ? void 0 : item.seqId.toString()) !== null && _b !== void 0 ? _b : '',
                                    conversation_id: reqTrackerContext.conversationId,
                                    uuid: reqTrackerContext.uuid,
                                    error_msg: (_d = (_c = item === null || item === void 0 ? void 0 : item.response) === null || _c === void 0 ? void 0 : _c.error_desc) !== null && _d !== void 0 ? _d : '',
                                    error_code: (_g = (_f = (_e = item === null || item === void 0 ? void 0 : item.response) === null || _e === void 0 ? void 0 : _e.status_code) === null || _f === void 0 ? void 0 : _f.toString()) !== null && _g !== void 0 ? _g : '',
                                    url_path: (_h = item === null || item === void 0 ? void 0 : item.url) !== null && _h !== void 0 ? _h : '',
                                    net_type: (_k = (_j = item === null || item === void 0 ? void 0 : item.method) === null || _j === void 0 ? void 0 : _j.toString()) !== null && _k !== void 0 ? _k : '',
                                    logid: (_l = item === null || item === void 0 ? void 0 : item.logid) !== null && _l !== void 0 ? _l : '',
                                    is_retry_req: options.isRetryReq ? '1' : '0'
                                });
                                return resp;
                            }
                            throw new BaseError({
                                ctx: this.ctx,
                                msg: `${(_m = ErrorType[errType]) !== null && _m !== void 0 ? _m : 'unknown'}:${resp.error_desc}`,
                                type: errType,
                                sender: this,
                                logid: resp === null || resp === void 0 ? void 0 : resp.log_id,
                                args: {
                                    req: body,
                                    resp
                                }
                            });
                    }
                }
                if (resp.body) {
                    this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.NetworkRequest, {
                        duration: Monitor.performanceNow() - startTime,
                        retry_times: (_o = item.retryTimes) !== null && _o !== void 0 ? _o : 0
                    }, {
                        imsdk_cmd: cmd.toString(),
                        imsdk_result: '1',
                        seq_id: item.seqId.toString(),
                        conversation_id: reqTrackerContext.conversationId,
                        uuid: reqTrackerContext.uuid,
                        error_msg: '',
                        error_code: '0',
                        url_path: (_p = item.url) !== null && _p !== void 0 ? _p : '',
                        net_type: (_r = (_q = item.method) === null || _q === void 0 ? void 0 : _q.toString()) !== null && _r !== void 0 ? _r : '',
                        logid: (_s = resp.log_id) !== null && _s !== void 0 ? _s : '',
                        is_retry_req: options.isRetryReq ? '1' : '0'
                    });
                    return resp;
                }
                else {
                    throw new BaseError({
                        ctx: this.ctx,
                        msg: 'no response body',
                        type: ErrorType.Unknown,
                        sender: this,
                        logid: resp === null || resp === void 0 ? void 0 : resp.log_id,
                        args: {
                            req: body,
                            resp
                        }
                    });
                }
            }
            catch (err) {
                this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.NetworkRequest, {
                    duration: Monitor.performanceNow() - startTime,
                    retry_times: (_t = item === null || item === void 0 ? void 0 : item.retryTimes) !== null && _t !== void 0 ? _t : 0
                }, {
                    imsdk_cmd: cmd.toString(),
                    imsdk_result: '0',
                    seq_id: (_u = item === null || item === void 0 ? void 0 : item.seqId.toString()) !== null && _u !== void 0 ? _u : '',
                    conversation_id: reqTrackerContext.conversationId,
                    uuid: reqTrackerContext.uuid,
                    error_msg: (_w = (_v = item === null || item === void 0 ? void 0 : item.response) === null || _v === void 0 ? void 0 : _v.error_desc) !== null && _w !== void 0 ? _w : '',
                    error_code: (_z = (_y = (_x = item === null || item === void 0 ? void 0 : item.response) === null || _x === void 0 ? void 0 : _x.status_code) === null || _y === void 0 ? void 0 : _y.toString()) !== null && _z !== void 0 ? _z : '',
                    url_path: (_0 = item === null || item === void 0 ? void 0 : item.url) !== null && _0 !== void 0 ? _0 : '',
                    net_type: (_2 = (_1 = item === null || item === void 0 ? void 0 : item.method) === null || _1 === void 0 ? void 0 : _1.toString()) !== null && _2 !== void 0 ? _2 : '',
                    logid: (_3 = item === null || item === void 0 ? void 0 : item.logid) !== null && _3 !== void 0 ? _3 : '',
                    is_retry_req: options.isRetryReq ? '1' : '0'
                });
                if (err === undefined) {
                    throw new BaseError({
                        ctx: this.ctx,
                        msg: 'unknown error',
                        type: ErrorType.Unknown,
                        sender: this,
                        args: {
                            req: body
                        }
                    });
                }
                if (err.type !== undefined) {
                    err.args = Object.assign(Object.assign({}, err.args), { req: body });
                    throw err;
                }
                else {
                    throw new BaseError({
                        ctx: this.ctx,
                        msg: 'request unknown error',
                        type: ErrorType.Unknown,
                        innerError: err,
                        sender: this,
                        args: {
                            req: body
                        }
                    });
                }
            }
        });
    }
    getReqTrackerContext(req) {
        var _a, _b, _c, _d;
        for (const key of Object.keys(req)) {
            const reqBody = req[key];
            if (reqBody !== undefined) {
                return {
                    conversationId: (_b = (_a = reqBody.conversation_id) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '',
                    uuid: (_d = (_c = reqBody.client_message_id) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : ''
                };
            }
        }
        return {
            conversationId: '',
            uuid: ''
        };
    }
}

export { BaseApiManager };
//# sourceMappingURL=BaseApiManager.js.map
